import React, { ReactNode } from 'react'

import { Box, Card, CardProps, CircularProgress, Stack, Typography } from '@mui/material'
import { alpha, styled } from '@mui/material/styles'

import { fCurrency, fShortenNumber } from '../../utils/formatNumber'
import Iconify from '../Iconify/Iconify'
import { InfoButton } from '../InfoButton'

import { ChangeIndicator } from './ChangeIndicator'

// ----------------------------------------------------------------------

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}))

// ----------------------------------------------------------------------

// Define the props type for the component
interface AppWidgetSummaryProps extends Omit<CardProps, 'title'> {
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' // You can expand the color options if needed
  customColors?: {
    main: string
    background: string
    iconBackground: string
    borderColor?: string
  }
  icon: string
  title: string
  children: ReactNode
  isLoading?: boolean
  type?: 'number' | 'string' | 'money' | 'percent' | 'component'
  explanation?: string | null
  changePercent?: number | null
  previousValue?: number | null
  details?: ReactNode
}

export const WidgetSummary: React.FC<AppWidgetSummaryProps> = ({
  title,
  children,
  icon,
  color = 'primary',
  customColors,
  type = 'number',
  sx,
  isLoading = false,
  explanation,
  changePercent,
  previousValue,
  details,
  ...other
}) => {
  const renderChildren = () => {
    if (type === 'component') return children
    if (type === 'money') {
      const value = typeof children === 'number' ? children : parseFloat(String(children))
      /* TODO: Now we have exchange rates only in euro, change currency to USD after exchange service change */
      return fCurrency(value, { currency: 'EUR' })
    }
    if (type === 'percent') {
      const value = typeof children === 'number' ? children : parseFloat(String(children))
      return `${value.toFixed(1)}%`
    }
    if (type === 'number') {
      const value = typeof children === 'number' ? children : parseFloat(String(children))
      return fShortenNumber(value)
    }
    return String(children)
  }

  const formatTooltipValue = (value: number) => {
    switch (type) {
      case 'money':
        return fCurrency(value, { currency: 'EUR' })
      case 'percent':
        return `${value.toFixed(1)}%`
      case 'number':
        return value.toLocaleString()
      default:
        return value.toLocaleString()
    }
  }

  const numericValue = typeof children === 'number' ? children : null
  const isComponentCard = type === 'component'

  return (
    <Card
      sx={{
        py: 5,
        px: 3,
        boxShadow: 0,
        textAlign: 'center',
        position: 'relative',
        color: customColors?.main || ((theme) => theme.palette[color].main),
        bgcolor:
          customColors?.background ||
          ((theme) => alpha(theme.palette[color].main, theme.palette.action.selectedOpacity)),
        ...(customColors?.borderColor && {
          border: `1px solid ${customColors.borderColor}`,
        }),
        ...sx,
      }}
      {...other}
    >
      {explanation && (
        <Box sx={{ position: 'absolute', top: 15, right: 15 }}>
          <InfoButton explanation={explanation} />
        </Box>
      )}
      {isLoading ? (
        <CircularProgress disableShrink size={30} />
      ) : (
        <>
          {isComponentCard ? (
            <Stack
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: '100%' }}
            >
              <IconWrapperStyle
                sx={{
                  color: customColors?.main || ((theme) => theme.palette[color].main),
                  marginBottom: 0,
                  backgroundImage:
                    customColors?.iconBackground ||
                    ((theme) =>
                      `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0)} 0%, ${alpha(
                        theme.palette[color].main,
                        0.24,
                      )} 100%)`),
                }}
              >
                <Iconify icon={icon} width={24} height={24} />
              </IconWrapperStyle>
              {children}
              <Typography variant="subtitle2">{title}</Typography>
            </Stack>
          ) : (
            <>
              <IconWrapperStyle
                sx={{
                  color: customColors?.main || ((theme) => theme.palette[color].main),
                  backgroundImage:
                    customColors?.iconBackground ||
                    ((theme) =>
                      `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0)} 0%, ${alpha(
                        theme.palette[color].main,
                        0.24,
                      )} 100%)`),
                }}
              >
                <Iconify icon={icon} width={24} height={24} />
              </IconWrapperStyle>
              <Stack spacing={1} alignItems="center">
                <Typography variant="h3">{renderChildren()}</Typography>
                <ChangeIndicator
                  value={changePercent}
                  currentValue={numericValue}
                  previousValue={previousValue ?? null}
                  formatValue={formatTooltipValue}
                />
              </Stack>
              <Typography variant="subtitle2">{title}</Typography>
              {details ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block', whiteSpace: 'pre-line' }}
                >
                  {details}
                </Typography>
              ) : null}
            </>
          )}
        </>
      )}
    </Card>
  )
}

export default WidgetSummary
